import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { ExternalLink, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Image as ImageIcon, Loader2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ButtonTip } from "@/components/ui/button-tip";
import { Input } from "@/components/ui/input";
import { type LeadProfile } from "../../OnboardingAPI";
import { Field } from "../Fields";
import { Collapse, usePrefersReducedMotion } from "../calm";
import { dragHasFiles } from "../dropFiles";
import { safeSourceUrl, useAddressLookup } from "../useAddressLookup";
import { cn } from "@/lib/utils";
import { StepBody, StepFooter, StepHeading } from "../StepCard";
import { FIELD_MAX, LOGO_SLOT, filesOfSlot, formatBytes, logoSlot, companyReady } from "../onboardingModel";
import { numberedCount } from "../steps";
import type { StepContext } from "./shared";

/** Step 1: who you are. The fields, the contact, the address and the logo. */
export default function CompanyStep({ ctx }: { ctx: StepContext }) {
  const { c } = ctx;
  const { t } = useTranslation();
  const view = c.view!;
  const values = c.values!;
  const profile = values.profile;
  const logo = logoSlot(view.slots);
  const logoFile = filesOfSlot(view.files, LOGO_SLOT)[0];
  const logoPending = c.uploads.find((item) => item.slot === LOGO_SLOT);
  const logoBusy = !!logoPending && (logoPending.status === "waiting" || logoPending.status === "uploading");
  // once the server checks the mobile number, the number is required here and confirmed in a step of its own
  const phoneRequired = !!view.phone;
  const ready = companyReady(values, phoneRequired);

  // an address suggestion from the company name (only where the server offers it)
  const lookup = useAddressLookup({
    enabled: view.lookup?.enabled === true && view.canEdit,
    token: c.token,
    companyName: profile.companyName ?? "",
    profile,
    update: (field, value) => c.updateProfile(field, value),
  });

  // the logo takes a drop of its own: a counter (not the children's leave events) keeps the hover from flickering
  const reduced = usePrefersReducedMotion();
  const [logoOver, setLogoOver] = useState(0);
  const [dropNote, setDropNote] = useState<string | null>(null);
  const [dropMessage, setDropMessage] = useState("");
  const logoInput = useRef<HTMLInputElement>(null);
  const extensionOfName = (name: string) => (name.includes(".") ? name.split(".").pop()!.toLowerCase() : "");
  const onLogoDrop = (event: DragEvent) => {
    if (!dragHasFiles(event.nativeEvent)) return;
    // handled here and nowhere else: the page-wide drop must not take it as well
    event.preventDefault();
    event.stopPropagation();
    setLogoOver(0);
    if (!logo || !view.canEdit) return;
    const dropped = Array.from(event.dataTransfer.files ?? []).filter((file) => file.size > 0 || file.type !== "");
    const allowed = new Set(logo.extensions.map((extension) => extension.replace(/^\./, "").toLowerCase()));
    const fitting = dropped.filter((file) => allowed.has(extensionOfName(file.name)));
    setDropNote(null);
    if (fitting.length === 0) {
      // a type the logo does not take: the same sentence as for a wrong file chosen by hand
      const sentence = c.errorText("invalid_file_type", logo);
      c.setSlotNotices((previous) => ({ ...previous, [LOGO_SLOT]: sentence }));
      setDropMessage(sentence);
      return;
    }
    void c.pickLogo(fitting[0], logo);
    setDropMessage(t("onboardingPage.logo.dropTaken", { name: fitting[0].name }));
    if (dropped.length > 1) setDropNote(t("onboardingPage.logo.dropOnlyOne", { name: fitting[0].name }));
  };
  const logoDropHandlers = {
    onDragEnter: (event: DragEvent) => {
      if (!dragHasFiles(event.nativeEvent)) return;
      event.preventDefault();
      setLogoOver((n) => n + 1);
    },
    onDragOver: (event: DragEvent) => {
      if (!dragHasFiles(event.nativeEvent)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (event: DragEvent) => {
      if (!dragHasFiles(event.nativeEvent)) return;
      setLogoOver((n) => Math.max(0, n - 1));
    },
    onDrop: onLogoDrop,
  };
  const isOver = logoOver > 0;

  const text = (field: keyof LeadProfile, options: { type?: string; autoComplete?: string; required?: boolean; className?: string; inputMode?: "tel"; hint?: string; readOnly?: boolean } = {}) => {
    const id = `onboarding-${field}`;
    const message = c.problemText(field, profile[field] as string | undefined);
    return (
      <Field id={id} label={t(`onboardingPage.company.${field}`)} required={options.required} error={message} hint={options.hint} className={options.className}>
        <Input
          id={id}
          type={options.type ?? "text"}
          inputMode={options.inputMode}
          autoComplete={options.autoComplete}
          readOnly={options.readOnly}
          data-highlight={lookup.highlighted.includes(field) ? "true" : undefined}
          className={lookup.highlighted.includes(field) ? cn("ring-2 ring-primary/60", !reduced && "transition-shadow duration-300") : undefined}
          value={(profile[field] as string | undefined) ?? ""}
          maxLength={FIELD_MAX[field]}
          aria-invalid={message ? true : undefined}
          aria-describedby={message ? `${id}-error` : options.hint ? `${id}-hint` : undefined}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            if (field === "companyName") lookup.onNameInput();
            c.updateProfile(field, event.target.value);
          }}
          onBlur={() => c.touch(field)}
        />
      </Field>
    );
  };

  return (
    <>
      <StepBody>
        <StepHeading
          eyebrow={t("onboardingPage.nav.stepOf", { current: ctx.step.index, total: numberedCount(ctx.steps) })}
          title={t("onboardingPage.titles.company")}
          lead={t("onboardingPage.company.lead")}
          focus={ctx.focus}
        />
        <Collapse open={!!ctx.nudge}>
          <p role="status" className="rounded-xl bg-muted/60 px-4 py-3 text-sm">{ctx.nudge}</p>
        </Collapse>

        <fieldset className="space-y-4">
          <legend className="mb-2 text-sm font-semibold">{t("onboardingPage.company.contactTitle")}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {text("companyName", { required: true, autoComplete: "organization", className: "sm:col-span-2" })}
            <div className="grid gap-4 sm:col-span-2 sm:grid-cols-2" data-testid="contact-names">
              {text("contactFirstName", { required: true, autoComplete: "given-name" })}
              {text("contactLastName", { required: true, autoComplete: "family-name" })}
              <p className="text-xs text-muted-foreground sm:col-span-2" data-testid="contact-names-hint">
                {t("onboardingPage.company.contactNameHint")}
              </p>
            </div>
            {text("contactEmail", { type: "email", autoComplete: "email", readOnly: true, hint: t("onboardingPage.company.contactEmailHint") })}
            {text("contactPhone", {
              type: "tel",
              autoComplete: "tel",
              inputMode: "tel",
              required: phoneRequired,
              hint: phoneRequired ? t("onboardingPage.company.contactPhoneHint") : undefined,
            })}
          </div>
        </fieldset>

        {logo && (
          <div className="rounded-2xl border border-border p-4" data-testid="logo-area">
            <h2 className="text-sm font-semibold">{t("onboardingPage.logo.title")}</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t(view.limits.maxLogoBytes ? "onboardingPage.logo.hintSize" : "onboardingPage.logo.hint", {
                list: logo.extensions.map((extension) => extension.replace(/^\./, "").toUpperCase()).join(", "),
                size: view.limits.maxLogoBytes ? formatBytes(view.limits.maxLogoBytes, c.locale) : "",
              })}
            </p>
            <div
              data-testid="logo-dropzone"
              data-over={isOver ? "true" : "false"}
              {...(view.canEdit ? logoDropHandlers : {})}
              className={cn(
                "mt-3 flex flex-wrap items-center gap-4 rounded-xl border border-dashed p-3 transition-[transform,background-color,border-color] duration-200",
                isOver ? "border-primary bg-primary/5" : "border-border",
                isOver && !reduced && "scale-[1.02]",
                // while a file is over, the children take no pointer: no enter/leave storm, no flicker
                isOver && "[&_*]:pointer-events-none",
              )}
            >
              <div className="flex h-16 w-28 items-center justify-center overflow-hidden rounded-xl border border-dashed border-border bg-muted/40">
                {c.logoPreview ? (
                  <img key={c.logoPreview} src={c.logoPreview} alt="" className="onb-fade max-h-full max-w-full object-contain" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-2 [&>p]:min-h-6">
                {logoBusy && (
                  <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t("onboardingPage.logo.uploading")}
                  </p>
                )}
                {logoPending?.status === "error" && (
                  <p role="alert" className="text-sm text-muted-foreground">
                    {c.errorText(logoPending.errorCode ?? "unknown", logo, logoPending.retryAfterSeconds)}
                  </p>
                )}
                {logoFile && !logoBusy && (
                  <p className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                    <span className="truncate" title={logoFile.originalName}>
                      {t("onboardingPage.logo.uploaded", { name: logoFile.originalName })}
                    </span>
                  </p>
                )}
                {dropNote && (
                  <p role="status" className="onb-fade text-sm text-muted-foreground" data-testid="logo-drop-note">
                    {dropNote}
                  </p>
                )}
                <span className="sr-only" role="status" aria-live="polite" data-testid="logo-drop-live">{dropMessage}</span>
                {c.slotNotices[LOGO_SLOT] && (
                  <p role="alert" className="text-sm text-muted-foreground">
                    {c.slotNotices[LOGO_SLOT]}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <p className="text-sm text-muted-foreground" data-testid="logo-drop-text">
                    {isOver ? t("onboardingPage.logo.dropActive") : t("onboardingPage.logo.dropHere")}
                  </p>
                  <ButtonTip label={t("onboardingPage.tips.chooseLogo")}>
                  <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-input px-3 text-sm font-medium transition-colors hover:bg-accent focus-within:ring-2 focus-within:ring-ring max-sm:h-11">
                    {logoFile ? t("onboardingPage.logo.replace") : t("onboardingPage.logo.choose")}
                    <input
                      ref={logoInput}
                      type="file"
                      className="sr-only"
                      data-testid="upload-logo"
                      accept={logo.extensions.map((extension) => `.${extension.replace(/^\./, "")}`).join(",")}
                      onChange={(event) => {
                        const chosen = event.target.files?.[0];
                        event.target.value = "";
                        void c.pickLogo(chosen, logo);
                      }}
                    />
                  </label>
                  </ButtonTip>
                  {logoFile && (
                    <ButtonTip label={t("onboardingPage.tips.removeLogo")}>
                    <Button type="button" variant="ghost" size="sm" onClick={() => void c.removeLogo(logoFile)}>
                      <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      {t("onboardingPage.logo.remove")}
                    </Button>
                    </ButtonTip>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {lookup.phase === "found" && lookup.suggestion && (
          <div role="group" aria-label={t("onboardingPage.lookup.title")} className="onb-soft rounded-2xl border border-border bg-white p-4 shadow-sm dark:bg-card" data-testid="lookup-card">
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0 flex-1 space-y-2">
                <h2 className="text-sm font-semibold">{t("onboardingPage.lookup.title")}</h2>
                <address className="text-sm not-italic leading-6 [overflow-wrap:anywhere]" data-testid="lookup-address">
                  {[
                    lookup.suggestion.data.legalName,
                    [lookup.suggestion.data.street, lookup.suggestion.data.streetNumber].filter(Boolean).join(" "),
                    [lookup.suggestion.data.postalCode, lookup.suggestion.data.city].filter(Boolean).join(" "),
                    lookup.suggestion.data.country,
                    lookup.suggestion.data.website,
                  ]
                    .filter((line) => !!line && String(line).trim())
                    .map((line, index) => (
                      <span key={index} className="block">{line}</span>
                    ))}
                </address>
                {lookup.suggestion.source?.host && (
                  <p className="text-xs text-muted-foreground" data-testid="lookup-source">
                    {t("onboardingPage.lookup.source")}{" "}
                    {safeSourceUrl(lookup.suggestion.source.url) ? (
                      <a href={safeSourceUrl(lookup.suggestion.source.url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline underline-offset-2">
                        {lookup.suggestion.source.host}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    ) : (
                      lookup.suggestion.source.host
                    )}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">{t("onboardingPage.lookup.note")}</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <ButtonTip label={t("onboardingPage.tips.lookupAdopt")}><Button type="button" size="sm" onClick={lookup.adopt} data-testid="lookup-adopt">{t("onboardingPage.lookup.adopt")}</Button></ButtonTip>
                  <ButtonTip label={t("onboardingPage.tips.lookupDecline")}><Button type="button" size="sm" variant="ghost" onClick={lookup.decline} data-testid="lookup-decline">{t("onboardingPage.lookup.decline")}</Button></ButtonTip>
                </div>
              </div>
            </div>
          </div>
        )}

        <fieldset className="space-y-4">
          <legend className="mb-2 text-sm font-semibold">{t("onboardingPage.company.addressTitle")}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {text("legalName", { className: "sm:col-span-2" })}
            {text("street", { autoComplete: "address-line1" })}
            {text("streetNumber")}
            {text("postalCode", { autoComplete: "postal-code" })}
            {text("city", { autoComplete: "address-level2" })}
            {text("country", { autoComplete: "country-name" })}
            {text("website", { type: "url", autoComplete: "url" })}
          </div>
        </fieldset>
      </StepBody>
      <StepFooter onBack={ctx.back} onNext={ctx.next} nextDisabled={!ready || logoBusy} nextReason={logoBusy ? t("onboardingPage.tips.waitLogo") : t("onboardingPage.tips.companyFirst")} hint={logoBusy ? t("onboardingPage.nav.waitUploads") : null} />
    </>
  );
}
